// src/pages/IvrStates.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Workflow, Plus, Edit2, Trash2, X, Search } from 'lucide-react';
import { stateDetailsService, StateDetail, CallRequest, ActionType, ContextType } from 'api/services/stateDetails.service';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const INPUT = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
const CALL_REQ: CallRequest[] = ['inbound', 'outbound'];
const ACTIONS: ActionType[] = ['static', 'api_call', 'function_call'];
const CONTEXTS: ContextType[] = ['bypass', 'dtmf', 'endcall', 'stream', 'transfer', 'voice_mail'];

const empty = (): StateDetail => ({
  operator_id: '', service_id: '', name: '', language: 'en', type: 'dtmf',
  action: 'static', context_type: 'dtmf', call_request: 'inbound',
  is_blocked: 0, business_name: 'default_business', attributes: '',
  action_details: '', type_meta: '{\n  "choices": []\n}'
});

const choiceCount = (tm?: string) => { try { const o = JSON.parse(tm || '{}'); return Array.isArray(o.choices) ? o.choices.length : '—'; } catch { return '—'; } };

const IvrStates: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canManage = ['master_admin', 'super_admin', 'service_admin', 'platform_admin', 'provider_owner', 'provider_admin', 'operator_admin', 'business_owner', 'business_admin'].includes(role || '');

  const [searchParams] = useSearchParams();
  // Pre-fill the filter when arriving from the IVR Apps "View states" link.
  const [filter, setFilter] = useState({
    operatorId: searchParams.get('operatorId') || '',
    serviceId: searchParams.get('serviceId') || '',
  });
  const [rows, setRows] = useState<StateDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await stateDetailsService.list({ operatorId: filter.operatorId || undefined, serviceId: filter.serviceId || undefined });
    if (res.success) setRows(res.data || []); else setError(res.error || 'Failed to load');
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<StateDetail | null>(null);
  const [form, setForm] = useState<StateDetail>(empty());
  const [formErr, setFormErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm({ ...empty(), operator_id: filter.operatorId, service_id: filter.serviceId }); setFormErr(null); setModal(true); };
  const openEdit = (s: StateDetail) => { setEditing(s); setForm({ ...empty(), ...s }); setFormErr(null); setModal(true); };

  const save = async () => {
    setFormErr(null);
    for (const k of ['operator_id', 'service_id', 'name'] as const) {
      if (!String(form[k] || '').trim()) { setFormErr(`${k} is required`); return; }
    }
    // type_meta should be valid JSON text (it's stored raw but warn on bad JSON)
    if (form.type_meta && form.type_meta.trim()) { try { JSON.parse(form.type_meta); } catch { setFormErr('type_meta is not valid JSON'); return; } }
    setSaving(true);
    const payload = { ...form, call_request: form.call_request || null };
    const res = editing?.id ? await stateDetailsService.update(editing.id, payload) : await stateDetailsService.create(payload);
    setSaving(false);
    if (res.success) { setModal(false); load(); } else setFormErr(res.error || 'Save failed');
  };
  const remove = async (s: StateDetail) => {
    if (!s.id || !window.confirm(`Delete state "${s.name}"?`)) return;
    const res = await stateDetailsService.remove(s.id);
    if (res.success) load(); else alert(res.error || 'Delete failed');
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Workflow className="w-6 h-6 text-blue-600" /> IVR Flow States</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">The agent/IVR state machine the runtime follows (operator + service). Aligned to the live schema.</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs text-gray-500 mb-1">Operator ID</label><input value={filter.operatorId} onChange={e => setFilter({ ...filter, operatorId: e.target.value })} className={`${INPUT} md:w-48`} placeholder="e.g. TEEOFF" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">Service ID</label><input value={filter.serviceId} onChange={e => setFilter({ ...filter, serviceId: e.target.value })} className={`${INPUT} md:w-48`} placeholder="e.g. TEEAI" /></div>
        <button onClick={load} className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 inline-flex items-center gap-2"><Search className="w-4 h-4" /> Load</button>
        <div className="flex-1" />
        {canManage && <button onClick={openCreate} className="tl-btn tl-btn-primary"><Plus className="w-5 h-5" /> Add State</button>}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      {loading ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>{['State', 'Lang', 'Call', 'Type', 'Action', 'Context', 'Choices', 'Actions'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${h === 'Actions' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">No states. Filter by operator + service, or add one.</td></tr>}
                {rows.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{s.name}{s.is_blocked ? <span className="ml-2 text-xs text-red-500">(blocked)</span> : ''}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{s.language || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.call_request || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{s.type}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.action}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{s.context_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{choiceCount(s.type_meta)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(s)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit2 className="w-4 h-4" /></button>
                        {canManage && <button onClick={() => remove(s)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{editing ? 'Edit State' : 'Add State'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {formErr && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{formErr}</div>}
              <div className="grid grid-cols-3 gap-4">
                <F label="Operator ID"><input value={form.operator_id} onChange={e => setForm({ ...form, operator_id: e.target.value })} className={INPUT} placeholder="TEEOFF" /></F>
                <F label="Service ID"><input value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value })} className={INPUT} placeholder="TEEAI" /></F>
                <F label="State name"><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={INPUT} placeholder="MAIN_MENU" /></F>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <F label="Language"><input value={form.language || ''} onChange={e => setForm({ ...form, language: e.target.value })} className={INPUT} placeholder="en" /></F>
                <F label="Type"><input value={form.type || ''} onChange={e => setForm({ ...form, type: e.target.value })} className={INPUT} placeholder="dtmf" /></F>
                <F label="Action"><select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} className={INPUT}>{ACTIONS.map(x => <option key={x} value={x}>{x}</option>)}</select></F>
                <F label="Context"><select value={form.context_type} onChange={e => setForm({ ...form, context_type: e.target.value })} className={INPUT}>{CONTEXTS.map(x => <option key={x} value={x}>{x}</option>)}</select></F>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <F label="Call request"><select value={form.call_request || ''} onChange={e => setForm({ ...form, call_request: e.target.value })} className={INPUT}><option value="">(none)</option>{CALL_REQ.map(x => <option key={x} value={x}>{x}</option>)}</select></F>
                <F label="Business name"><input value={form.business_name || ''} onChange={e => setForm({ ...form, business_name: e.target.value })} className={INPUT} /></F>
                <F label="Attributes"><input value={form.attributes || ''} onChange={e => setForm({ ...form, attributes: e.target.value })} className={INPUT} /></F>
              </div>
              <F label="action_details (text)"><textarea value={form.action_details || ''} onChange={e => setForm({ ...form, action_details: e.target.value })} rows={2} className={`${INPUT} font-mono text-sm`} /></F>
              <F label="type_meta (JSON text — choices[].nextState)"><textarea value={form.type_meta || ''} onChange={e => setForm({ ...form, type_meta: e.target.value })} rows={7} className={`${INPUT} font-mono text-sm`} placeholder='{"choices":[{"key":"1","value":"1","msg":"1 for news","nextState":"NEWS"}]}' /></F>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300"><input type="checkbox" checked={!!form.is_blocked} onChange={e => setForm({ ...form, is_blocked: e.target.checked ? 1 : 0 })} className="w-4 h-4 rounded border-gray-300 text-blue-600" /> Blocked</label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">{saving && <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}{editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>{children}</div>
);

export default IvrStates;
